import React from "react";
import {
    TextField,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    RadioGroup,
    FormControlLabel,
    Radio,
    Box,
    Grid,
    Typography,
    Checkbox
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';

export const OralExaminationTab = ({
    formData,
    setFormData,
    handleChange,
    handleSubmit,
    activeDots,
    wrapperRef,
    dots,
    selectedValue,
    setSelectedValue,
    handleDotClickOral,
    getPatchLabelForDot,
    handleClickOutside,
    screeningStatus,
    handleOpenUpdateModal,
    setActiveWorkflowTab,
    setOpenConfirmDialog,
    today,
    isDhOrChc
}) => {
    const hasExamDataAvailable = Boolean(
        formData.exam?.abnormalPatches ||
        (formData.exam?.visualExam && Object.values(formData.exam.visualExam).some(arr => Array.isArray(arr) && arr.length > 0)) ||
        formData.exam?.cavity ||
        formData.exam?.chronicTrauma ||
        (formData.exam?.dentalCaries !== null && formData.exam?.dentalCaries !== '' && formData.exam?.dentalCaries !== false) ||
        (formData.exam?.dentures !== null && formData.exam?.dentures !== '' && formData.exam?.dentures !== false) ||
        (formData.exam?.lossOfTooth !== null && formData.exam?.lossOfTooth !== '' && formData.exam?.lossOfTooth !== false) ||
        (formData.exam?.fissures !== null && formData.exam?.fissures !== '' && formData.exam?.fissures !== false) ||
        (formData.exam?.induration !== null && formData.exam?.induration !== '' && formData.exam?.induration !== false) ||
        (formData.exam?.lymphadenopathy !== null && formData.exam?.lymphadenopathy !== '' && formData.exam?.lymphadenopathy !== false) ||
        formData.exam?.otherFindings
    );

    return (
        <>
            <Box sx={{ p: 2.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, borderBottom: '1px solid #eef2f5', bgcolor: '#fafafa', mb: 3 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography sx={{ fontWeight: 800, color: '#1e293b', fontSize: '15px' }}>
                            Status: {screeningStatus}
                        </Typography>
                        {!isDhOrChc && handleOpenUpdateModal && (
                            <Button
                                variant="text"
                                size="small"
                                sx={{ color: '#3f51b5', fontWeight: 700, minWidth: 'auto', p: 0, textTransform: 'none', textDecoration: 'underline' }}
                                onClick={handleOpenUpdateModal}
                            >
                                Update
                            </Button>
                        )}
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography sx={{ fontSize: '12px', color: '#3f51b5', fontWeight: 700 }}>
                            📖 Quick Links
                        </Typography>
                    </Box>
                </Box>

                <Box sx={{ display: 'flex', gap: 2 }}>
                    {/* FOLLOW UP Card */}
                    <Box
                        onClick={() => setActiveWorkflowTab(3)}
                        sx={{
                            width: '95px',
                            height: '75px',
                            border: '1px solid #cbd5e1',
                            borderRadius: '6px',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            bgcolor: '#fff',
                            transition: 'all 0.2s',
                            boxShadow: '0 2px 6px rgba(0,0,0,0.03)',
                            '&:hover': { bgcolor: '#9fa8da' }
                        }}
                    >
                        <Typography sx={{ fontSize: '20px', mb: 0.2 }}>📋</Typography>
                        <Typography sx={{ fontSize: '10px', fontWeight: 800, color: '#1e293b' }}>
                            FOLLOW UP
                        </Typography>
                    </Box>

                    {/* REFER Card */}
                    <Box
                        onClick={() => setActiveWorkflowTab(4)}
                        sx={{
                            width: '95px',
                            height: '75px',
                            border: '1px solid #cbd5e1',
                            borderRadius: '6px',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            bgcolor: '#fff',
                            transition: 'all 0.2s',
                            boxShadow: '0 2px 6px rgba(0,0,0,0.03)',
                            '&:hover': { bgcolor: '#9fa8da' }
                        }}
                    >
                        <Typography sx={{ fontSize: '20px', mb: 0.2 }}>👥</Typography>
                        <Typography sx={{ fontSize: '10px', fontWeight: 800, color: '#1e293b' }}>
                            REFER
                        </Typography>
                    </Box>
                </Box>
            </Box>

            <form onSubmit={handleSubmit}>
                {/* Header row with Title, Checkbox and Date Picker */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#334155', fontSize: '16px' }}>
                            Examination
                        </Typography>
                        {!hasExamDataAvailable && (
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={formData.exam.noAbnormalityFound}
                                        onChange={(e) => {
                                            if (e.target.checked) {
                                                setOpenConfirmDialog(true);
                                            } else {
                                                setFormData(prev => ({
                                                    ...prev,
                                                    exam: {
                                                        ...prev.exam,
                                                        noAbnormalityFound: false,
                                                        abnormalPatches: true
                                                    }
                                                }));
                                            }
                                        }}
                                        sx={{ color: '#cbd5e1', '&.Mui-checked': { color: '#3f51b5' } }}
                                    />
                                }
                                label={<Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#475569' }}>No Abnormality Detected</Typography>}
                            />
                        )}
                    </Box>

                    <Box sx={{ width: '180px' }}>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                                format="DD/MM/YYYY"
                                value={dayjs(formData.assessmentDate, 'YYYY-MM-DD')}
                                onChange={(newValue) => setFormData(prev => ({
                                    ...prev,
                                    assessmentDate: newValue ? newValue.format('YYYY-MM-DD') : today
                                }))}
                                maxDate={dayjs()}
                                disableFuture
                                slotProps={{
                                    textField: {
                                        size: 'small',
                                        sx: { bgcolor: '#fff', '& .MuiOutlinedInput-root': { borderRadius: '6px' } }
                                    }
                                }}
                            />
                        </LocalizationProvider>
                    </Box>
                </Box>

                {/* Category selector grid */}
                <Grid container spacing={2} sx={{ mb: 3, pointerEvents: formData.exam.noAbnormalityFound ? 'none' : 'auto', opacity: formData.exam.noAbnormalityFound ? 0.6 : 1 }}>
                    {[
                        { id: "Leukoplakia", label: "LEUKOPLAKIA", img: "/images/any_white_or_red_patch_in_mouth.png" },
                        { id: "Erythroplakia", label: "ERYTHROPLAKIA", img: "/images/any_white_or_red_patch_in_mouth.png" },
                        { id: "Ulcer", label: "ULCER", img: "/images/any_white_or_red_patch_in_mouth.png" },
                        { id: "Growth", label: "GROWTH", img: "/images/ulceration.png" },
                        { id: "LockedJaw", label: "LOCKED JAW", img: "/images/locked-jaw.png" },
                        { id: "Submucousfibrosis", label: "SUBMUCOUS FIBROSIS", img: "/images/oral-screening.png" },
                        { id: "Smokerspalate", label: "SMOKERS PALATE", img: "/images/any_white_or_red_patch_in_mouth.png" },
                        { id: "LichenPlanus", label: "LICHEN PLANUS", img: "/images/any_white_or_red_patch_in_mouth.png" }
                    ].map(({ id, label, img }) => {
                        const isSelected = selectedValue === id;
                        return (
                            <Grid item xs={12} sm={4} key={id}>
                                <Button
                                    fullWidth
                                    variant="outlined"
                                    onClick={() => setSelectedValue(prev => prev === id ? '' : id)}
                                    disabled={formData.exam.noAbnormalityFound}
                                    startIcon={
                                        <img
                                            src={img}
                                            alt=""
                                            style={{
                                                width: '24px',
                                                height: '24px',
                                                objectFit: 'contain',
                                                filter: isSelected ? 'none' : 'grayscale(20%)'
                                            }}
                                        />
                                    }
                                    sx={{
                                        py: 1.5,
                                        textTransform: 'none',
                                        fontWeight: 800,
                                        fontSize: '13px',
                                        justifyContent: 'flex-start',
                                        pl: 2,
                                        color: isSelected ? '#333333' : '#475569',
                                        bgcolor: isSelected ? '#9fa8da' : '#ffffff',
                                        borderColor: isSelected ? '#7986cb' : '#cbd5e1',
                                        borderWidth: isSelected ? '2px' : '1px',
                                        borderRadius: '4px',
                                        transition: 'all 0.2s',
                                        '&:hover': {
                                            bgcolor: isSelected ? '#8c98d4' : '#f8fafc',
                                            borderColor: isSelected ? '#7986cb' : '#cbd5e1',
                                            borderWidth: isSelected ? '2px' : '1px'
                                        }
                                    }}
                                >
                                    {label}
                                </Button>
                            </Grid>
                        );
                    })}
                </Grid>

                {/* Clock Mouth overlay locator */}
                <Box sx={{ border: '1px solid #cbd5e1', borderRadius: '4px', p: 3, mb: 3, pointerEvents: formData.exam.noAbnormalityFound ? 'none' : 'auto', opacity: formData.exam.noAbnormalityFound ? 0.6 : 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography sx={{ fontSize: '13px', color: '#475569', fontWeight: 700 }}>
                            Showing earlier findings
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <Button size="small" sx={{ textTransform: 'none', fontWeight: 700, color: '#3f51b5' }} onClick={() => { setSelectedValue(''); }}>
                                Show All Findings
                            </Button>
                            <Button size="small" sx={{ textTransform: 'none', fontWeight: 700, color: '#3f51b5' }} onClick={(e) => handleClickOutside(e)}>
                                Reset
                            </Button>
                        </Box>
                    </Box>

                    <Typography variant="subtitle2" sx={{ textAlign: 'center', fontWeight: 700, mb: 2, color: '#334155' }}>
                        Mark Location for : {selectedValue || "All Findings"}
                    </Typography>

                    <Box className="mouthDemo" sx={{ bgcolor: 'transparent' }}>
                        <div className="mouth-wrapper" ref={wrapperRef}>
                            {dots.map(({ id, value }) => (
                                <div
                                    key={id}
                                    className={`dot ${id} ${activeDots.includes(value) ? "selected" : ""}`}
                                    onClick={(e) => handleDotClickOral(value, e)}
                                >
                                    {activeDots.includes(value) && (
                                        <div className="dot-popup" style={{ whiteSpace: 'nowrap', zIndex: 10 }}>
                                            <p style={{ margin: 0, fontWeight: 700 }}>{getPatchLabelForDot(value)}</p>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </Box>
                </Box>

                {/* Flex-based Parameter layout */}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '24px', mt: 2, width: '100%', pointerEvents: formData.exam.noAbnormalityFound ? 'none' : 'auto', opacity: formData.exam.noAbnormalityFound ? 0.6 : 1 }}>
                    {/* Oral Cavity */}
                    <Box sx={{ width: { xs: '100%', md: 'calc(33.33% - 16px)' } }}>
                        <FormControl fullWidth size="small" disabled={formData.exam.noAbnormalityFound}>
                            <InputLabel>Oral Cavity</InputLabel>
                            <Select
                                name="cavity"
                                value={formData.exam.cavity || ''}
                                label="Oral Cavity"
                                onChange={handleChange}
                            >
                                <MenuItem value="">Select</MenuItem>
                                <MenuItem value="Healthy">Healthy</MenuItem>
                                <MenuItem value="Unhealthy">Unhealthy</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>

                    {/* Chronic Trauma */}
                    <Box sx={{ width: { xs: '100%', md: 'calc(33.33% - 16px)' } }}>
                        <FormControl fullWidth size="small" disabled={formData.exam.noAbnormalityFound}>
                            <InputLabel>Chronic Trauma</InputLabel>
                            <Select
                                name="chronicTrauma"
                                value={formData.exam.chronicTrauma || ''}
                                label="Chronic Trauma"
                                onChange={handleChange}
                            >
                                <MenuItem value="">Select</MenuItem>
                                <MenuItem value="Sharp Tooth">Sharp Tooth</MenuItem>
                                <MenuItem value="Ill fitting denture">Ill fitting denture</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>

                    {/* Dental Caries */}
                    <Box sx={{ width: { xs: '100%', md: 'calc(33.33% - 16px)' } }}>
                        <FormControl component="fieldset" disabled={formData.exam.noAbnormalityFound}>
                            <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5, color: '#334155' }}>Dental Caries</Typography>
                            <RadioGroup
                                row
                                name="dentalCaries"
                                value={formData.exam.dentalCaries === true ? "Yes" : formData.exam.dentalCaries === false ? "No" : ""}
                                onChange={handleChange}
                            >
                                <FormControlLabel value="Yes" control={<Radio size="small" />} label="Yes" />
                                <FormControlLabel value="No" control={<Radio size="small" />} label="No" />
                            </RadioGroup>
                        </FormControl>
                    </Box>

                    {/* Dentures */}
                    <Box sx={{ width: { xs: '100%', md: 'calc(33.33% - 16px)' } }}>
                        <FormControl component="fieldset" disabled={formData.exam.noAbnormalityFound}>
                            <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5, color: '#334155' }}>Dentures</Typography>
                            <RadioGroup
                                row
                                name="dentures"
                                value={formData.exam.dentures === true ? "Yes" : formData.exam.dentures === false ? "No" : ""}
                                onChange={handleChange}
                            >
                                <FormControlLabel value="Yes" control={<Radio size="small" />} label="Yes" />
                                <FormControlLabel value="No" control={<Radio size="small" />} label="No" />
                            </RadioGroup>
                        </FormControl>
                    </Box>

                    {/* Loss of Tooth */}
                    <Box sx={{ width: { xs: '100%', md: 'calc(33.33% - 16px)' } }}>
                        <FormControl component="fieldset" disabled={formData.exam.noAbnormalityFound}>
                            <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5, color: '#334155' }}>Loss of Tooth</Typography>
                            <RadioGroup
                                row
                                name="lossOfTooth"
                                value={formData.exam.lossOfTooth === true ? "Yes" : formData.exam.lossOfTooth === false ? "No" : ""}
                                onChange={handleChange}
                            >
                                <FormControlLabel value="Yes" control={<Radio size="small" />} label="Yes" />
                                <FormControlLabel value="No" control={<Radio size="small" />} label="No" />
                            </RadioGroup>
                        </FormControl>
                    </Box>

                    {/* Fissures */}
                    <Box sx={{ width: { xs: '100%', md: 'calc(33.33% - 16px)' } }}>
                        <FormControl component="fieldset" disabled={formData.exam.noAbnormalityFound}>
                            <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5, color: '#334155' }}>Fissures</Typography>
                            <RadioGroup
                                row
                                name="fissures"
                                value={formData.exam.fissures === true ? "Yes" : formData.exam.fissures === false ? "No" : ""}
                                onChange={handleChange}
                            >
                                <FormControlLabel value="Yes" control={<Radio size="small" />} label="Yes" />
                                <FormControlLabel value="No" control={<Radio size="small" />} label="No" />
                            </RadioGroup>
                        </FormControl>
                    </Box>

                    {/* Induration */}
                    <Box sx={{ width: { xs: '100%', md: 'calc(33.33% - 16px)' } }}>
                        <FormControl component="fieldset" disabled={formData.exam.noAbnormalityFound}>
                            <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5, color: '#334155' }}>Induration</Typography>
                            <RadioGroup
                                row
                                name="induration"
                                value={formData.exam.induration === true ? "Yes" : formData.exam.induration === false ? "No" : ""}
                                onChange={handleChange}
                            >
                                <FormControlLabel value="Yes" control={<Radio size="small" />} label="Yes" />
                                <FormControlLabel value="No" control={<Radio size="small" />} label="No" />
                            </RadioGroup>
                        </FormControl>
                    </Box>

                    {/* Lymphadenopathy */}
                    <Box sx={{ width: { xs: '100%', md: 'calc(33.33% - 16px)' } }}>
                        <FormControl component="fieldset" disabled={formData.exam.noAbnormalityFound}>
                            <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5, color: '#334155' }}>Lymphadenopathy</Typography>
                            <RadioGroup
                                row
                                name="lymphadenopathy"
                                value={formData.exam.lymphadenopathy === true ? "Yes" : formData.exam.lymphadenopathy === false ? "No" : ""}
                                onChange={handleChange}
                            >
                                <FormControlLabel value="Yes" control={<Radio size="small" />} label="Yes" />
                                <FormControlLabel value="No" control={<Radio size="small" />} label="No" />
                            </RadioGroup>
                        </FormControl>
                    </Box>

                    {/* Other Findings */}
                    <Box sx={{ width: { xs: '100%', md: 'calc(33.33% - 16px)' } }}>
                        <FormControl component="fieldset" fullWidth disabled={formData.exam.noAbnormalityFound}>
                            <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5, color: '#334155' }}>Other Findings</Typography>
                            <RadioGroup
                                row
                                name="otherFindings"
                                value={formData.exam.otherFindings === true ? "Yes" : formData.exam.otherFindings === false ? "No" : ""}
                                onChange={handleChange}
                            >
                                <FormControlLabel value="Yes" control={<Radio size="small" />} label="Yes" />
                                <FormControlLabel value="No" control={<Radio size="small" />} label="No" />
                            </RadioGroup>
                        </FormControl>
                        {formData.exam.otherFindings === true && (
                            <TextField
                                size="small"
                                label="Other Finding Description"
                                name="otherFindingsTxt"
                                value={formData.exam.otherFindingsTxt || ''}
                                onChange={handleChange}
                                disabled={formData.exam.noAbnormalityFound}
                                fullWidth
                                sx={{ mt: 1, '& .MuiOutlinedInput-root': { borderRadius: '6px' } }}
                            />
                        )}
                    </Box>

                    {/* Root Canal History (md=8 equivalent width) */}
                    <Box sx={{ width: { xs: '100%', md: 'calc(66.67% - 16px)' } }}>
                        <TextField
                            label="Root Canal History"
                            name="rootCanalHistory"
                            value={formData.exam.rootCanalHistory || ''}
                            onChange={handleChange}
                            disabled={formData.exam.noAbnormalityFound}
                            fullWidth
                            multiline
                            rows={2}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '6px' } }}
                        />
                    </Box>

                    {/* Site */}
                    <Box sx={{ width: { xs: '100%', md: 'calc(33.33% - 16px)' } }}>
                        <FormControl fullWidth size="small" disabled={formData.exam.noAbnormalityFound} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '6px' } }}>
                            <InputLabel>Site</InputLabel>
                            <Select
                                name="site"
                                value={formData.exam.site || ''}
                                label="Site"
                                onChange={handleChange}
                            >
                                <MenuItem value="">Select</MenuItem>
                                <MenuItem value="Supraclavicular">Supraclavicular</MenuItem>
                                <MenuItem value="Submandibular">Submandibular</MenuItem>
                                <MenuItem value="Other">Other</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>
                </Box>

                {/* Submit Button */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%', mt: 4 }}>
                    <Button
                        type="submit"
                        variant="contained"
                        sx={{
                            bgcolor: '#3f51b5',
                            color: '#fff',
                            fontWeight: 700,
                            textTransform: 'none',
                            px: 4.5,
                            py: 1,
                            borderRadius: '4px',
                            '&:hover': { bgcolor: '#303f9f' }
                        }}
                    >
                        Save & Continue
                    </Button>
                </Box>
            </form>
        </>
    );
};
